import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';

const getEventByID = (ID) => Axios.get(`/events/${ID}`);

const useEvent = (ID) => {
    const intID = Number(ID);
    return useQuery({
        queryKey: ['event', intID],
        queryFn: () => getEventByID(intID),
        staleTime: 30000,
        cacheTime: 30000,
        retry: false,
        refetchOnWindowFocus: false,
        enabled: ID ? true : false,
    });
};

const forceReloadEvent = (ID) => {
    const intID = Number(ID);
    queryClient.invalidateQueries({ queryKey: ['event', intID] });
};

const onRemoveEvent = ({ ID }) => Axios.delete(`/events/${ID}`);

const useRemoveEvent = () => {
    return useMutation({
        mutationFn: onRemoveEvent,
        onSuccess: (data, variables) => {},
    });
};

const onUpdateEvent = ({ ID, data }) => Axios.put(`/events/${ID}`, data);

const useUpdateEvent = () => {
    return useMutation({
        mutationFn: onUpdateEvent,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.ID);
        },
    });
};

const onSetRequest = ({ EventID, ModelID, OnRequest }) =>
    Axios.put('/eventdatesmodels/SetOnRequest', { EventID, ModelID, OnRequest });

const useSetRequest = () => {
    return useMutation({
        mutationFn: onSetRequest,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.EventID);
        },
    });
};

const onGetOptionNumber = (data) => Axios.post(`/eventdatesmodels/SuggestedOptionNumber`, data);

const onUpdateOptionNumber = ({ data }) => Axios.put(`/eventdatesmodels/UpdateOptionNumber`, data);

const useUpdateOptionNumber = () => {
    return useMutation({
        mutationFn: onUpdateOptionNumber,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.ID);
            if (variables.ProjectID) {
                forceReloadProject(variables.ProjectID);
                forceReloadProjectTransactions(variables.ProjectID);
            }
        },
    });
};

const onDeleteEventDatesModels = ({ eventDateModelId, force }) => {
    const url = force ? `/eventdatesmodels/${eventDateModelId}?force=true` : `/eventdatesmodels/${eventDateModelId}`;
    return Axios.delete(url);
};

const useDeleteEventDatesModels = () => {
    return useMutation({
        mutationFn: onDeleteEventDatesModels,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.ID);
            if (variables.projectID) {
                forceReloadProject(variables.projectID);
                forceReloadProjectTransactions(variables.projectID);
            }
        },
    });
};

const onDeleteEventDate = ({ eventDateId }) => Axios.delete(`/eventDates/${eventDateId}`);

const useDeleteEventDate = () => {
    return useMutation({
        mutationFn: onDeleteEventDate,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.ID);
        },
    });
};

const onChangeEventDateTime = async ({ data }) => {
    await Axios.put(`/eventdates/ChangeTime`, data);
};

const useChangeEventDateTime = () => {
    return useMutation({
        mutationFn: onChangeEventDateTime,
        onSuccess: (data, variables) => {},
    });
};

const onChangeEventDateModelTime = ({ data }) => Axios.put(`/eventdatesmodels/ChangeTime`, data);

const useChangeEventDateModeltime = () => {
    return useMutation({
        mutationFn: onChangeEventDateModelTime,
        onSuccess: (data, variables) => {},
    });
};

const onChangeEventDateNote = ({ data }) => Axios.put(`/eventdates/EditNotes`, data);

const useChangeEventDateNote = () => {
    return useMutation({
        mutationFn: onChangeEventDateNote,
        onSuccess: (data, variables) => {},
    });
};

const onChangeEventDateModelNote = ({ data }) => Axios.put(`/eventdatesmodels/EditNotes`, data);

const useChangeEventDateModelNote = () => {
    return useMutation({
        mutationFn: onChangeEventDateModelNote,
        onSuccess: (data, variables) => {},
    });
};

const onChangeEventDateModelAddress = ({ addressData }) => Axios.post('/eventdatesmodels/ChangeAddress', addressData);

const useChangeEventDateModelAddress = () => {
    return useMutation({
        mutationFn: onChangeEventDateModelAddress,
        onSuccess: (data, variables) => {},
    });
};

const onChangeEventDateAddress = ({ addressData }) => Axios.post('/eventDates/ChangeAddress', addressData);

const onChangeEventDateModels = (data) => Axios.put('/eventdatesmodels', data);

const onChangeEventDate = (data) => Axios.put('/eventdates/', data);

const useChangeEventDateAddress = () => {
    return useMutation({
        mutationFn: onChangeEventDateAddress,
        onSuccess: (data, variables) => {},
    });
};

const onCancelEventDateModel = ({ EventDateModelsIDs, Cancel }) =>
    Axios.put(`/eventdatesmodels/Cancel`, {
        EventDateModelsIDs,
        Cancel,
    });

const useCancelEventDateModel = () => {
    return useMutation({
        mutationFn: onCancelEventDateModel,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.eventId);
            //TODO: add reload project and project transactions
        },
    });
};

const onAcknowledgeEventDateModel = ({ eventDateModelId, acknowledge }) =>
    Axios.put(`/eventdatesmodels/Acknowledge/${eventDateModelId}/${acknowledge}`);

const useAcknowledgeEventDateModel = () => {
    return useMutation({
        mutationFn: onAcknowledgeEventDateModel,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.eventId);
        },
    });
};

const onAddModelToDate = ({ modelId, eventDateId, optionNumber }) => {
    const dataToSend = {
        ModelID: modelId,
        EventDateID: eventDateId,
    };
    if (optionNumber && optionNumber > 0) {
        dataToSend.OptionNumber = optionNumber;
    }

    return Axios.post('/eventdatesmodels/AddModelToDate', dataToSend);
};

const useAddModelToDate = () => {
    return useMutation({
        mutationFn: onAddModelToDate,
        onSuccess: (data, variables) => {
            if (variables.eventId) {
                forceReloadEvent(variables.eventId);
            }
        },
    });
};

const onAddModelToDates = ({ modelId, eventDatesIds, optionNumber }) => {
    const eventDatesToSend = eventDatesIds.map((eventDateId) => ({
        ID: eventDateId,
        OptionNumber: optionNumber && optionNumber > 0 ? optionNumber : null,
    }));
    const dataToSend = {
        ModelID: modelId,
        EventDates: [...eventDatesToSend],
    };
    return Axios.post('/eventdatesmodels/AddModelToDates', dataToSend);
    // if (optionNumber && optionNumber > 0) {
    //     dataToSend.OptionNumber = optionNumber;
    // }
    // return (Axios.post("/eventdatesmodels/AddModelToDates", {
    //     ModelID: modelId,
    //     EventDateIDs: [...eventDateIds]
    // }))
};

const useAddModelToDates = () => {
    return useMutation({
        mutationFn: onAddModelToDates,
        onSuccess: (data, variables) => {
            if (variables.eventId) {
                forceReloadEvent(variables.eventId);
            }
        },
    });
};

/* event address */
const getEventAddressByID = (ID) => Axios.get(`/eventaddresses/${ID}`);

const useEventAddress = (ID) => {
    const intID = Number(ID);
    return useQuery({
        queryKey: ['eventAddress', intID],
        queryFn: () => getEventAddressByID(intID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const onAddEventAddress = ({ data }) => Axios.post(`eventaddresses`, data);

const useAddEventAddress = () => {
    return useMutation({
        mutationFn: onAddEventAddress,
        onSuccess: (data, variables) => {},
    });
};

const forceReloadEventAddress = (ID) => {
    const intID = Number(ID);
    queryClient.invalidateQueries({ queryKey: ['eventAddress', intID] });
};

const onAddEventDate = ({ data }) => Axios.post('/eventDates/AddEventDate', data);

const useAddEventDate = () => {
    return useMutation({
        mutationFn: onAddEventDate,
        onSuccess: (data, variables) => {},
    });
};

const onCancelEvent = ({ ID, cancel }) => Axios.put(`/events/Cancel/${ID}/${cancel}`);

const useCancelEvent = () => {
    return useMutation({
        mutationFn: onCancelEvent,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.ID);
        },
    });
};

const onUpdateEventCustomFields = ({ eventId, data }) => Axios.put(`/events/Customfields/${eventId}`, data);

const useUpdateEventCustomFields = () => {
    return useMutation({
        mutationFn: onUpdateEventCustomFields,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.eventId);
        },
    });
};

const onMoveBookingEvent = ({ eventData }) => Axios.put(`/eventdatesmodels/moveDate`, eventData);

const useMoveBookingEvent = () => {
    return useMutation({
        mutationFn: onMoveBookingEvent,
        onSuccess: (data, variables) => {
            forceReloadEvent(variables.eventData.ID);
        },
    });
};

export {
    useEvent,
    forceReloadEvent,
    useSetRequest,
    useRemoveEvent,
    useUpdateEvent,
    onGetOptionNumber,
    useUpdateOptionNumber,
    useDeleteEventDatesModels,
    useDeleteEventDate,
    useChangeEventDateTime,
    useChangeEventDateModeltime,
    useChangeEventDateNote,
    useChangeEventDateModelNote,
    useAddEventAddress,
    useEventAddress,
    forceReloadEventAddress,
    useChangeEventDateModelAddress,
    useChangeEventDateAddress,
    useCancelEventDateModel,
    useAcknowledgeEventDateModel,
    useAddEventDate,
    useCancelEvent,
    useAddModelToDate,
    useAddModelToDates,
    useUpdateEventCustomFields,
    onChangeEventDateModels,
    useMoveBookingEvent,
    onChangeEventDate,
    //useEventsWallIds,
    //refetchEventWallIds
};
